import { Accordion, Button, Group } from '@mantine/core';
import { IconPencil, IconPlus, IconStairs, IconTrash } from '@tabler/icons-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { RowActions } from 'src/Components/Shared/RowActions';
import { trackBedPanelOpened } from 'src/Services/Mixpanel/events';
import { RoomDto } from 'src/types/cartography.types';

import useBuildingContent, { FloorGroup } from '../../hooks/useBuildingContent';
import { isSleepingRoomType } from '../../referential';
import { FloorBand } from '../FloorBand';

import styles from './BuildingCard.module.scss';
import { Props } from './BuildingCard.types';

// Pick the accordion item opened by default: ground floor if we can find it,
// otherwise the first floor in the current sort order.
const pickDefaultOpen = (groups: FloorGroup[]): string | null => {
  const ground = groups.find(g => g.floor?.level === 0);
  if (ground?.floor) return ground.floor.id;
  const first = groups[0];
  return first?.floor?.id ?? (first ? 'no-floor' : null);
};

const BuildingCard = ({
  building,
  summary,
  onOpenRoom,
  onEditBuilding,
  onDeleteBuilding,
}: Props) => {
  const { t } = useTranslation();

  const handleRoomCreated = (created: RoomDto) => {
    if (!isSleepingRoomType(created.type)) return;
    trackBedPanelOpened({ source: 'after_room_create' });
    onOpenRoom(created.id, { openBedForm: true });
  };

  const {
    hasFloors,
    groups,
    enSuiteLabelOf,
    openCreateFloor,
    openEditFloor,
    openCreateRoom,
    handleEditRoom,
    handleToggleOutOfService,
    handleDeleteRoom,
    handleDeleteFloor,
    drawers,
  } = useBuildingContent(building, 'asc', handleRoomCreated);

  const defaultOpen = useMemo(() => pickDefaultOpen(groups), [groups]);
  const typeLabel = building.type
    ? t(`admin.structure.buildingTypes.${building.type}`, building.type)
    : null;
  const hasContent = building.rooms.length > 0 || building.floors.length > 0;

  const renderBand = (group: FloorGroup) => (
    <FloorBand
      floor={group.floor}
      rooms={group.rooms}
      showHeader={false}
      onAddRoom={openCreateRoom}
      onEditFloor={openEditFloor}
      onDeleteFloor={handleDeleteFloor}
      onOpenRoom={onOpenRoom}
      onEditRoom={handleEditRoom}
      onToggleOutOfService={handleToggleOutOfService}
      onDeleteRoom={handleDeleteRoom}
      enSuiteLabelOf={enSuiteLabelOf}
    />
  );

  return (
    <div className={styles.card}>
      <div className={styles.summary}>
        <div>
          <h3 className={styles.title}>
            {building.name}
            {typeLabel && <span className={styles.typeChip}>· {typeLabel}</span>}
          </h3>
          <div className={styles.subline}>{summary}</div>
        </div>
        <div className={styles.actions}>
          <RowActions
            actions={[
              {
                key: 'edit',
                label: t('common.edit', 'Modifier'),
                icon: <IconPencil size={18} />,
                onClick: () => onEditBuilding(building),
              },
              {
                key: 'delete',
                label: t('common.delete', 'Supprimer'),
                icon: <IconTrash size={18} />,
                variant: 'danger',
                onClick: () => onDeleteBuilding(building),
              },
            ]}
          />
        </div>
      </div>

      {hasContent ? (
        <>
          <Group gap="xs" className={styles.addFloor}>
            <Button
              variant="primary"
              size="sm"
              leftSection={<IconPlus size={16} />}
              onClick={() => openCreateRoom(null)}
            >
              {t('admin.structure.rooms.add', 'Ajouter une pièce')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              leftSection={<IconStairs size={16} />}
              onClick={openCreateFloor}
            >
              {t('admin.structure.floors.add', 'Ajouter un niveau')}
            </Button>
          </Group>

          {hasFloors ? (
            <Accordion
              multiple
              defaultValue={defaultOpen ? [defaultOpen] : []}
              className={styles.accordion}
              variant="separated"
            >
              {groups.map(group => {
                const value = group.floor?.id ?? 'no-floor';
                const label =
                  group.floor?.name ?? t('admin.structure.floors.noFloor', 'Sans niveau');
                const activeRoomCount = group.rooms.filter(room => !room.isOutOfService).length;
                return (
                  <Accordion.Item key={value} value={value}>
                    <Accordion.Control>
                      <span>{label}</span>{' '}
                      <span style={{ color: 'var(--mantine-color-dimmed)' }}>
                        ·{' '}
                        {t('admin.structure.rooms.count', {
                          count: activeRoomCount,
                          defaultValue: '{{count}} pièce',
                          defaultValue_other: '{{count}} pièces',
                        })}
                      </span>
                    </Accordion.Control>
                    <Accordion.Panel>{renderBand(group)}</Accordion.Panel>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          ) : (
            <div className={styles.floors}>{groups.map(renderBand)}</div>
          )}
        </>
      ) : (
        <div className={styles.empty}>
          {t('admin.structure.rooms.empty', 'Aucune pièce dans ce bâtiment')}
          <Group gap="xs" className={styles.addFloor}>
            <Button
              variant="primary"
              size="sm"
              leftSection={<IconPlus size={16} />}
              onClick={() => openCreateRoom(null)}
            >
              {t('admin.structure.rooms.add', 'Ajouter une pièce')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              leftSection={<IconStairs size={16} />}
              onClick={openCreateFloor}
            >
              {t('admin.structure.floors.add', 'Ajouter un niveau')}
            </Button>
          </Group>
        </div>
      )}

      {drawers}
    </div>
  );
};

export default BuildingCard;
